import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Carousel, CloseButton, Spinner } from 'react-bootstrap';
import './NotifyResidentModal.css';

const isVideoUrl = (url) => {
  return [".mp4", ".mov", ".webm", ".ogg", ".m4v"].some(ext => url.toLowerCase().endsWith(ext));
};

function NotifyResidentModal({
  show,
  handleClose,
  handleSend,
  residentEmail,
  setResidentEmail,
  customMessage,
  setCustomMessage,
  unitData,
  buildingId,
  buildingName,
  hvacType = 'fancoil'
}) {
  const [showCarousel, setShowCarousel] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sendLoading, setSendLoading] = useState(false);
  const [validMedia, setValidMedia] = useState({});
  const [photoToIssueMap, setPhotoToIssueMap] = useState({});

  useEffect(() => {
    if (!unitData) return;
    if (unitData.inspection === 'OK') {
      setCustomMessage(
        `Hello,\n\nThis is a notice that your maintenance was done for unit ${unitData.unit}, and no issues were found.\n\nThank you,\nHudson HVAC`
      );
    } else if (unitData.inspection === 'Missed') {
      setCustomMessage(
        `Hello,\n\nThis is a notice that your unit ${unitData.unit} remained inaccessible after two attempts from Hudson during the recent building maintenance cycle. As a result, your in-suite HVAC equipment was not inspected.\n\nWe recommend scheduling maintenance directly with Hudson at your earliest convenience to keep your equipment in good working order.\n\nThank you,\nHudson HVAC`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitData]);

  // Build photo-to-issue mapping from rawData (new format only)
  useEffect(() => {
    // Check for new format: either dataFormat is "new" or rawData exists (fallback for new format)
    if (unitData?.rawData) {
      const map = {};
      const issueNameMap = {
        "mould": "Potential Mould",
        "drip_pan": "Drain Pan",
        "drip_pan_sensor": "Drain Pan",
        "drip_pan__sensor": "Drain Pan",
        "thermostat": "Thermostat",
        "actuator": "Actuator",
        "3_way_valve": "3-Way Valve",
        "field_3_way_valves": "3-Way Valve",
        "n3way_valves": "3-Way Valve",
        "valves": "Valves",
        "hoses": "Hoses",
        "coils": "Coils",
        "compressor": "Compressor",
        "blower_fan": "Blower Fan",
        "bathroom_fan": "Bathroom Fan",
        "booster_fan": "Booster Fan",
        "exhaust_hose": "Exhaust Hose",
        "leak_sensor": "Leak Sensor",
        "dryer": "Dryer",
        "insulation": "Insulation",
        "other": "Other",
        "custom": "Other",
        "custom_issue": "Other",
        "custom issue": "Other",
        "any_other_custom_issues": "Other",
        "anyothercustomissues": "Other",
        "any other custom issues": "Other",
        "anyOtherCustomIssues": "Other"
      };
      
      const getIssueNameFromKey = (key) => {
        const keyLower = key.toLowerCase();
        for (const [fieldKey, displayName] of Object.entries(issueNameMap)) {
          if (keyLower.includes(fieldKey)) {
            return displayName;
          }
        }
        const match = keyLower.match(/photo_of_([^_]+)_issue/);
        if (match) {
          const issueKey = match[1];
          return issueNameMap[issueKey] || issueKey.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
        }
        return null;
      };
      
      Object.keys(unitData.rawData).forEach(key => {
        const keyLower = key.toLowerCase();
        if (keyLower.includes("model_sticker") || keyLower.includes("sticker_plate")) {
          return;
        }
        
        if (key.startsWith("photo_of") || key.startsWith("before_photo_of") || key.startsWith("after_photo_of")) {
          const value = unitData.rawData[key];
          const issueName = getIssueNameFromKey(key);
          
          // Determine photo type
          let photoType = "issue";
          if (key.startsWith("before_photo_of")) {
            photoType = "before";
          } else if (key.startsWith("after_photo_of")) {
            photoType = "after";
          }
          
          if (Array.isArray(value)) {
            value.forEach(url => {
              if (url && typeof url === "string") {
                if (!map[url]) map[url] = { issues: [], type: photoType };
                if (issueName && !map[url].issues.includes(issueName)) {
                  map[url].issues.push(issueName);
                }
                // Update type if this is a before/after photo
                if (photoType !== "issue") {
                  map[url].type = photoType;
                }
              }
            });
          } else if (value && typeof value === "string") {
            if (!map[value]) map[value] = { issues: [], type: photoType };
            if (issueName && !map[value].issues.includes(issueName)) {
              map[value].issues.push(issueName);
            }
            // Update type if this is a before/after photo
            if (photoType !== "issue") {
              map[value].type = photoType;
            }
          }
        } else if (key === "additional_photos_of_the_unit") {
          const value = unitData.rawData[key];
          if (Array.isArray(value)) {
            value.forEach(url => {
              if (url && typeof url === "string") {
                map[url] = { issues: ["General"], type: "issue" };
              }
            });
          } else if (value && typeof value === "string") {
            map[value] = { issues: ["General"], type: "issue" };
          }
        }
      });
      
      setPhotoToIssueMap(map);
    } else {
      setPhotoToIssueMap({});
    }
  }, [unitData]);

  if (!unitData) return null;

  const openCarousel = (index) => {
    setActiveIndex(index);
    setShowCarousel(true);
  };

  const closeCarousel = () => {
    setShowCarousel(false);
  };

  const isMissed = unitData.inspection === 'Missed';
  const hideFullReportButton = unitData.inspection === 'OK' || isMissed;

  const checkMediaValidity = async (url) => {
    if (!url) return false;
    if (validMedia.hasOwnProperty(url)) {
      return validMedia[url];
    }
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const isValid = response.ok;
      setValidMedia(prev => ({ ...prev, [url]: isValid }));
      return isValid;
    } catch (error) {
      setValidMedia(prev => ({ ...prev, [url]: false }));
      return false;
    }
  };

  const filteredPhotos = unitData?.photos?.filter(url => validMedia[url] !== false) || [];

  const onSubmit = async (e) => {
    e.preventDefault();
    setSendLoading(true);
    try {
      await handleSend(e);
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      dialogClassName="notify-resident-modal-dialog"
      contentClassName="notify-resident-modal-content"
    >
      <div className="inv-card" style={{ position: 'relative' }}>
        <CloseButton
          onClick={handleClose}
          style={{ position: 'absolute', top: 15, right: 15, zIndex: 1000 }}
        />
        <div style={{ marginBottom: '0.5rem' }}>
          <img
            src="https://hudsonhvac.ca/images/logo.png"
            alt="Hudson Logo"
            style={{ maxWidth: '78px', marginBottom: 0 }}
          />
        </div>
        <h2 className="modal-title">
          Notify Resident for Unit {unitData.unit}
        </h2>
        <p className="modal-subtitle">
          {isMissed
            ? 'Unit was inaccessible after two attempts. Encourage the resident to schedule maintenance directly.'
            : hideFullReportButton
              ? `No issues found for this ${hvacType === 'dryer-vent' ? 'vent' : 'fancoil'}.`
              : 'Review the issues and notify the resident via email.'}
        </p>
      </div>

      <Modal.Body>
        {filteredPhotos.length > 0 && (
          <div className="modal-thumbnail-container mb-4">
            {filteredPhotos.map((url, i) => (
              <button type="button" className="media-thumbnail-button" key={i} onClick={() => openCarousel(i)} aria-label={`View inspection photo ${i + 1}`}>
                {isVideoUrl(url) ? (
                  <video src={url} className="modal-thumbnail" muted onError={() => setValidMedia(prev => ({ ...prev, [url]: false }))} />
                ) : (
                  <img src={url} alt="Inspection" className="modal-thumbnail" onError={() => setValidMedia(prev => ({ ...prev, [url]: false }))} />
                )}
              </button>
            ))}
          </div>
        )}

        <Modal show={showCarousel} onHide={closeCarousel} centered size="lg" className="maintenance-photo-modal">
          <Modal.Header closeButton><Modal.Title>Inspection photos</Modal.Title></Modal.Header>
          <Modal.Body className="p-0">
            <Carousel
              className="modal-carousel"
              activeIndex={activeIndex}
              onSelect={setActiveIndex}
              interval={null}
            >
              {filteredPhotos.map((url, i) => {
                // Handle both old format (array) and new format (object)
                let photoInfo;
                if (photoToIssueMap[url]) {
                  if (Array.isArray(photoToIssueMap[url])) {
                    // Old format: convert to new format
                    photoInfo = { issues: photoToIssueMap[url], type: "issue" };
                  } else {
                    // New format: already an object
                    photoInfo = photoToIssueMap[url];
                  }
                } else {
                  photoInfo = { issues: [], type: "issue" };
                }
                
                const issues = photoInfo.issues || [];
                const photoType = photoInfo.type || "issue";
                
                let bannerText = "";
                if (issues.length > 0 && issues[0] !== "General") {
                  const issueName = issues[0];
                  if (hvacType === 'dryer-vent' && photoType === "before") {
                    bannerText = `Before Photo of ${issueName}`;
                  } else if (hvacType === 'dryer-vent' && photoType === "after") {
                    bannerText = `After Photo of ${issueName}`;
                  } else {
                    bannerText = `Photo of ${issueName} Issue`;
                  }
                }
                
                // Determine if this is an issue photo (not before/after)
                const isIssuePhoto = photoType === "issue" || (hvacType !== 'dryer-vent');
                
                return (
                  <Carousel.Item key={i}>
                    <div style={{ position: 'relative' }}>
                      {bannerText && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          backgroundColor: isIssuePhoto 
                            ? 'rgba(255, 241, 240, 0.9)' 
                            : 'rgba(17, 36, 62, 0.9)',
                          color: isIssuePhoto 
                            ? '#d92d20' 
                            : 'white',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontWeight: isIssuePhoto 
                            ? 'bold' 
                            : 600,
                          border: isIssuePhoto 
                            ? '1px solid #d92d20' 
                            : 'none',
                          zIndex: 10,
                          whiteSpace: 'nowrap'
                        }}>
                          {bannerText}
                        </div>
                      )}
                      {isVideoUrl(url) ? (
                        <video controls src={url} className="d-block w-100" onError={() => setValidMedia(prev => ({ ...prev, [url]: false }))} />
                      ) : (
                        <img src={url} alt="Inspection" className="d-block w-100" onError={() => setValidMedia(prev => ({ ...prev, [url]: false }))} />
                      )}
                    </div>
                  </Carousel.Item>
                );
              })}
            </Carousel>
          </Modal.Body>
        </Modal>

        <div className="mb-4 issue-summary">
          <h5>
            Issue Summary
            {isMissed && (
              <>
                <span style={{ color: '#000' }}>: </span>
                <span style={{ color: '#d97706', fontWeight: 'bold' }}>
                  Blocked After 2 Visits
                </span>
              </>
            )}
            {!isMissed && unitData?.dataFormat === "new" && unitData?.issues && unitData.issues.length > 0 && (
              <>
                <span style={{ color: '#000' }}>: </span>
                <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                  {unitData.issues.join(', ')}
                </span>
              </>
            )}
          </h5>
          <p>
            {isMissed
              ? (unitData.reasonMissed || unitData.notes || 'Unit was inaccessible after two attempts.')
              : (unitData.notes || (hvacType === 'dryer-vent' ? 'Vents Fully Functional' : 'Fancoils Fully Functional'))}
          </p>
        </div>

        <Form onSubmit={onSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="resident-notice">
              Resident Notice 
              <span>
                <p className='notice-subtext'>You may modify the notice below</p>
              </span>
            </Form.Label>
            <Form.Control
              className='textarea-form'
              as="textarea"
              rows={6}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="resident-notice">
              Resident's Email <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              type="email"
              placeholder="resident@example.com"
              value={residentEmail}
              onChange={(e) => setResidentEmail(e.target.value)}
              required
            />
          </Form.Group>

          <div className="notify-resident-actions d-flex gap-2">
            <Button
              type="submit"
              className="invite-submit-btn flex-grow-1"
              style={{ flexBasis: '65%' }}
              disabled={sendLoading}
            >
              {sendLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: '0.5rem' }}
                  />
                  Sending...
                </>
              ) : (
                'Send Resident Notice'
              )}
            </Button>

            {!hideFullReportButton && (
              <Button
                variant="outline-primary"
                className="flex-grow-1 full-report"
                style={{ flexBasis: '35%' }}
                onClick={() => window.open(
                  `https://hudsonhvac.ca/unit-report.html?unit=${unitData.unit}&a=${buildingId}&add=${encodeURIComponent(buildingName)}${hvacType === 'dryer-vent' ? '&type=dryer' : ''}`,
                  '_blank'
                )}
              >
                Full Report
              </Button>
            )}
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default NotifyResidentModal;
